import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
// Publishable key from window._env_ (local-config.js or env-config.js).
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

// Initialize Stripe with publishable key - use window._env_ first
// Don't use centralizedConfig at module level to avoid initialization issues
// Load Stripe lazily to avoid initialization errors at module load time
// TODO: cfg is app-specific config, removed for platform-core
const publishableKey = (typeof window !== 'undefined' && window._env_ && window._env_.STRIPE_PUBLISHABLE_KEY) || 
                       null;

// Load Stripe lazily - don't call loadStripe at module level to avoid errors
// Will be loaded when component mounts
// eslint-disable-next-line no-unused-vars -- reserved for Stripe Elements
let stripePromise = null;

const CardForm = ({ onSubmit, onCancel, onError, loading, costPoolData, setupIntentClientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const setErrorAndNotify = (msg) => {
    setError(msg);
    if (typeof onError === 'function') {
      onError();
    }
  };

  // Add error boundary for PaymentElement
  useEffect(() => {
    const handleError = (event) => {
      console.error('Stripe Elements error:', event);
      if (event.error) {
        setError(event.error.message || 'An error occurred with the payment form');
      }
    };

    // Listen for Stripe errors
    window.addEventListener('stripe-error', handleError);
    return () => {
      window.removeEventListener('stripe-error', handleError);
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Submit elements first to validate the form
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorAndNotify(submitError.message);
        setProcessing(false);
        return;
      }

      // If we have a setup intent, confirm it; otherwise create payment method directly
      if (setupIntentClientSecret) {
        const { error: confirmError, setupIntent } = await stripe.confirmSetup({
          clientSecret: setupIntentClientSecret,
          elements,
          confirmParams: {
            return_url: new URL('/?tab=cost-pools', window.location.origin).toString(),
          },
          redirect: 'if_required',
        });

        if (confirmError) {
          const isNetworkError = /network|fetch|ERR_NETWORK|connection/i.test(confirmError.message || '');
          setErrorAndNotify(isNetworkError
            ? 'We are experiencing issues connecting to our payments provider. Please check your internet connection and try again.'
            : confirmError.message);
          setProcessing(false);
          return;
        }

        if (setupIntent.status === 'succeeded' && setupIntent.payment_method) {
          const pm = setupIntent.payment_method;
          const pmId = typeof pm === 'string' ? pm : (pm?.id != null ? (typeof pm.id === 'string' ? pm.id : (pm.id?.id ?? null)) : null);
          if (pmId) {
            await onSubmit(pmId, {});
          } else {
            setErrorAndNotify('Payment method setup incomplete');
            setProcessing(false);
          }
        } else {
          setErrorAndNotify('Payment method setup incomplete');
          setProcessing(false);
        }
      } else {
        // This should not happen if setupIntentClientSecret is properly provided
        // But handle it gracefully as a fallback
        setErrorAndNotify('Payment setup not initialized. Please close and try again.');
        setProcessing(false);
      }
    } catch (err) {
      const msg = err?.message || '';
      const isNetworkError = (err?.name === 'TypeError' && (msg.includes('fetch') || msg.includes('network'))) ||
        /ERR_NETWORK|network.*changed|connection/i.test(msg);
      setErrorAndNotify(isNetworkError
        ? 'We are experiencing issues connecting to our payments provider. Please check your internet connection and try again.'
        : (msg || 'An unexpected error occurred'));
      setProcessing(false);
    }
  };

  if (!stripe || !elements) {
    return (
      <div className="loading-message">
        Loading payment form...
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="stripe-card-form">
      <div className="form-group">
        <label>Payment Method</label>
        <div className="payment-element-container">
          {stripe && elements ? (
            <PaymentElement
              options={{
                layout: 'tabs',
                paymentMethodOrder: ['card', 'apple_pay', 'google_pay', 'link'],
              }}
              onReady={() => {
                console.log('PaymentElement ready');
              }}
              onChange={(event) => {
                if (event.error) {
                  setError(event.error.message);
                } else {
                  setError(null);
                }
              }}
            />
          ) : (
            <div className="loading-message">Loading payment form...</div>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="form-actions">
        <button 
          type="submit"
          className="btn btn-primary"
          disabled={!stripe || !elements || processing}
        >
          {processing ? 'Processing...' : 'Add Payment Method'}
        </button>
      </div>

      <style>{`
        .stripe-card-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
          background: transparent;
        }

        .stripe-card-form .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stripe-card-form .form-group label {
          font-weight: 500;
          color: var(--text-primary, #1f2937);
          font-size: 0.95rem;
          margin-bottom: 6px;
          display: block;
        }

        .stripe-card-form .form-group input,
        .stripe-card-form .form-group select {
          padding: 12px 14px;
          border: 1px solid var(--border, #e5e7eb);
          border-radius: 8px;
          font-size: 15px;
          background-color: var(--bg-primary, #ffffff);
          color: var(--text-primary, #1f2937);
          transition: all 0.2s ease;
          width: 100%;
          font-family: inherit;
        }

        .stripe-card-form .form-group input:focus,
        .stripe-card-form .form-group select:focus {
          outline: none;
          border-color: var(--accent, #2563eb);
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
        }

        .stripe-card-form .form-group input::placeholder {
          color: var(--text-secondary, #6b7280);
        }

        .stripe-card-form .form-group input:disabled,
        .stripe-card-form .form-group select:disabled {
          background-color: var(--bg-secondary, #f3f4f6);
          color: var(--text-secondary, #6b7280);
          cursor: not-allowed;
        }

        .stripe-card-form .form-section {
          margin-bottom: 24px;
        }

        .stripe-card-form .section-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary, #1f2937);
          margin: 0 0 16px 0;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--border, #e5e7eb);
        }

        .stripe-card-form .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        @media (max-width: 768px) {
          .stripe-card-form {
            gap: 16px;
            padding: 0;
          }

          .stripe-card-form .form-group {
            gap: 6px;
          }

          .stripe-card-form .form-group label {
            font-size: 0.9rem;
          }

          .stripe-card-form .form-group input,
          .stripe-card-form .form-group select {
            padding: 10px 12px;
            font-size: 16px; /* Prevents zoom on iOS */
          }

          .stripe-card-form .form-row {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .stripe-card-form .form-actions {
            flex-direction: column !important;
            gap: 12px !important;
            margin-top: 20px !important;
            padding-top: 0 !important;
          }

          .stripe-card-form .btn {
            width: 100% !important;
            min-width: unset !important;
            padding: 0.875rem 1.5rem !important;
            font-size: 1rem !important;
          }

          .stripe-card-form .payment-element-container {
            width: 100%;
            overflow-x: hidden;
          }
        }

        @media (max-width: 480px) {
          .stripe-card-form .form-actions {
            gap: 10px !important;
          }

          .stripe-card-form .btn {
            padding: 0.875rem 1.25rem !important;
          }
        }

        .stripe-card-form .payment-element-container {
          padding: 0;
          border: none;
          background-color: transparent !important;
          width: 100%;
          box-sizing: border-box;
        }

        /* Stripe Elements mobile styling */
        .stripe-card-form .payment-element-container iframe {
          width: 100% !important;
          max-width: 100% !important;
        }

        /* Ensure Stripe tabs are mobile-friendly */
        @media (max-width: 768px) {
          .stripe-card-form .payment-element-container [class*="Tab"] {
            font-size: 0.9rem !important;
            padding: 10px 12px !important;
          }

          .stripe-card-form .payment-element-container [class*="Input"] {
            font-size: 16px !important; /* Prevents zoom on iOS */
            padding: 12px !important;
          }
        }

        .stripe-card-form .error-message {
          padding: 12px 16px;
          background-color: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          color: var(--error, #dc2626);
          font-size: 0.9em;
        }

        .stripe-card-form .loading-message {
          padding: 12px;
          text-align: center;
          color: var(--text-secondary, #6b7280);
          font-size: 0.9em;
        }

        .stripe-card-form .form-actions {
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          gap: 0 !important;
          margin-top: 2rem !important;
          padding-top: 0 !important;
          border-top: none !important;
          flex-wrap: wrap !important;
          width: 100% !important;
          box-sizing: border-box !important;
          background: transparent !important;
          backdrop-filter: none !important;
          box-shadow: none !important;
          position: static !important;
          bottom: auto !important;
          left: auto !important;
          right: auto !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          padding: 0 !important;
          z-index: auto !important;
        }

        .stripe-card-form .btn {
          padding: 0.875rem 2rem !important;
          font-size: 1rem !important;
          font-weight: 600 !important;
          border: none !important;
          border-radius: 12px !important;
          cursor: pointer !important;
          transition: all 0.3s ease !important;
          min-width: 120px !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif !important;
          line-height: 1.5 !important;
          text-align: center !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          box-sizing: border-box !important;
          background: transparent !important;
          backdrop-filter: none !important;
          box-shadow: none !important;
          position: static !important;
          width: auto !important;
          max-width: none !important;
        }

        .stripe-card-form .btn:disabled {
          opacity: 0.6 !important;
          cursor: not-allowed !important;
        }

        .stripe-card-form .btn-secondary {
          background-color: rgba(255, 255, 255, 0.1) !important;
          color: #ffffff !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
        }

        .stripe-card-form .btn-secondary:hover:not(:disabled) {
          background-color: rgba(255, 255, 255, 0.15) !important;
          border-color: rgba(255, 255, 255, 0.3) !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }

        .stripe-card-form .btn-secondary:active:not(:disabled) {
          transform: translateY(0) !important;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1) !important;
        }

        .stripe-card-form .btn-primary {
          background: linear-gradient(135deg, #5897de 0%, #4a85c9 100%) !important;
          color: #ffffff !important;
          border: none !important;
          box-shadow: 0 4px 12px rgba(88, 151, 222, 0.3) !important;
        }

        .stripe-card-form .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #4a85c9 0%, #3a75b9 100%) !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 6px 16px rgba(88, 151, 222, 0.4) !important;
        }

        .stripe-card-form .btn-primary:active:not(:disabled) {
          transform: translateY(0) !important;
          box-shadow: 0 2px 8px rgba(88, 151, 222, 0.3) !important;
        }

        .stripe-card-form .btn:disabled {
          background-color: rgba(255, 255, 255, 0.1) !important;
          color: rgba(255, 255, 255, 0.4) !important;
          border-color: rgba(255, 255, 255, 0.1) !important;
          box-shadow: none !important;
          transform: none !important;
        }
      `}</style>
    </form>
  );
};

const StripeCardForm = ({ onSubmit, onCancel, onError, loading, costPoolData, setupIntentClientSecret }) => {
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeError, setStripeError] = useState(null);
  const [currentStripePromise, setCurrentStripePromise] = useState(null);

  // Initialize Stripe lazily when component mounts
  useEffect(() => {
    if (!publishableKey) {
      setStripeError('Stripe is not configured. Please set STRIPE_PUBLISHABLE_KEY in your configuration.');
      setStripeReady(false);
      return;
    }

    // Load Stripe only when component mounts (not at module level)
    let isMounted = true;
    const initStripe = async () => {
      try {
        setStripeReady(false);
        setStripeError(null);
        
        // Create Stripe promise only when needed
        const promise = loadStripe(publishableKey);
        setCurrentStripePromise(promise);
        
        // Verify Stripe promise resolves
        const stripe = await promise;
        if (isMounted && stripe) {
          setStripeReady(true);
          setStripeError(null);
        } else if (isMounted) {
          setStripeError('Stripe failed to initialize');
          setStripeReady(false);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Stripe initialization error:', err);
          setStripeError(`Failed to load Stripe.js: ${err?.message || 'Unknown error'}`);
          setStripeReady(false);
          setCurrentStripePromise(null);
        }
      }
    };

    initStripe();

    return () => {
      isMounted = false;
    };
  }, []); // Only run once on mount

  // Re-check when clientSecret changes
  useEffect(() => {
    if (setupIntentClientSecret && currentStripePromise) {
      setStripeReady(false);
      setStripeError(null);
      
      // Re-verify Stripe is still valid
      let isMounted = true;
      const checkStripe = async () => {
        try {
          const stripe = await currentStripePromise;
          if (isMounted && stripe) {
            setStripeReady(true);
          } else if (isMounted) {
            setStripeError('Stripe failed to initialize');
            setStripeReady(false);
          }
        } catch (err) {
          if (isMounted) {
            console.error('Stripe verification error:', err);
            setStripeError(`Failed to load Stripe: ${err?.message || 'Unknown error'}`);
            setStripeReady(false);
          }
        }
      };
      
      checkStripe();
      return () => {
        isMounted = false;
      };
    }
  }, [setupIntentClientSecret, currentStripePromise]);

  // Early return if Stripe is not configured
  if (!publishableKey) {
    return (
      <div className="error-message">
        Stripe is not configured. Please set STRIPE_PUBLISHABLE_KEY in your configuration.
      </div>
    );
  }

  // Show error if Stripe failed to load
  if (stripeError) {
    return (
      <div className="error-message">
        {stripeError}
      </div>
    );
  }

  // Show loading state while SetupIntent is being created or Stripe is initializing
  if (!setupIntentClientSecret || !stripeReady) {
    return (
      <div className="loading-message">
        Initializing payment form...
      </div>
    );
  }

  // Validate clientSecret format (should start with seti_)
  if (typeof setupIntentClientSecret !== 'string' || !setupIntentClientSecret.startsWith('seti_')) {
    return (
      <div className="error-message">
        Invalid payment setup configuration. Please try again.
      </div>
    );
  }

  // Theme-aware appearance: light form on light page, dark on dark
  const isDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  const options = {
    clientSecret: setupIntentClientSecret,
    appearance: isDark
      ? {
          theme: 'night',
          variables: {
            colorPrimary: '#5897de',
            colorBackground: '#0a3a6a',
            colorText: '#ffffff',
            colorTextSecondary: 'rgba(255, 255, 255, 0.7)',
            colorDanger: '#ef4444',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            spacingUnit: '4px',
            borderRadius: '8px',
            colorIconTab: 'rgba(255, 255, 255, 0.7)',
            colorIconTabHover: '#ffffff',
            colorIconTabSelected: '#5897de',
          },
          rules: {
            '.Input': { backgroundColor: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.2)', color: '#ffffff' },
            '.Input:focus': { borderColor: '#5897de', boxShadow: '0 0 0 2px rgba(88, 151, 222, 0.3)' },
            '.Input::placeholder': { color: 'rgba(255, 255, 255, 0.5)' },
            '.Label': { color: '#ffffff' },
            '.Tab': { backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.15)', color: 'rgba(255, 255, 255, 0.7)' },
            '.Tab:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 255, 255, 0.25)', color: '#ffffff' },
            '.Tab--selected': { backgroundColor: 'rgba(88, 151, 222, 0.15)', borderColor: '#5897de', color: '#5897de' },
            '.TabIcon': { fill: 'rgba(255, 255, 255, 0.7)' },
            '.TabIcon--selected': { fill: '#5897de' },
            '.Block': { backgroundColor: 'rgba(255, 255, 255, 0.03)', borderColor: 'rgba(255, 255, 255, 0.1)' },
          },
        }
      : {
          theme: 'flat',
          variables: {
            colorPrimary: '#2563eb',
            colorBackground: '#ffffff',
            colorText: '#1f2937',
            colorTextSecondary: '#6b7280',
            colorDanger: '#dc2626',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            spacingUnit: '4px',
            borderRadius: '8px',
            colorIconTab: '#6b7280',
            colorIconTabHover: '#374151',
            colorIconTabSelected: '#2563eb',
          },
          rules: {
            '.Input': { backgroundColor: '#ffffff', border: '1px solid #e5e7eb', color: '#1f2937' },
            '.Input:focus': { borderColor: '#2563eb', boxShadow: '0 0 0 2px rgba(37, 99, 235, 0.2)' },
            '.Input::placeholder': { color: '#9ca3af' },
            '.Label': { color: '#374151' },
            '.Tab': { backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', color: '#6b7280' },
            '.Tab:hover': { backgroundColor: '#f3f4f6', borderColor: '#d1d5db', color: '#374151' },
            '.Tab--selected': { backgroundColor: '#eff6ff', borderColor: '#2563eb', color: '#2563eb' },
            '.TabIcon': { fill: '#6b7280' },
            '.TabIcon--selected': { fill: '#2563eb' },
            '.Block': { backgroundColor: '#f9fafb', borderColor: '#e5e7eb' },
          },
        },
    // Note: paymentMethodTypes and wallets are not needed when using clientSecret
  };

  // Use currentStripePromise (loaded lazily) instead of module-level stripePromise
  if (!currentStripePromise) {
    return (
      <div className="loading-message">
        Loading payment form...
      </div>
    );
  }

  return (
    <Elements 
      key={setupIntentClientSecret} 
      stripe={currentStripePromise} 
      options={options}
    >
      <CardForm 
        onSubmit={onSubmit} 
        onCancel={onCancel} 
        onError={onError}
        loading={loading} 
        costPoolData={costPoolData}
        setupIntentClientSecret={setupIntentClientSecret}
      />
    </Elements>
  );
};

export default StripeCardForm;
